<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    content?: string
    disabled?: boolean
  }>(),
  {
    content: '',
    disabled: false
  }
)

const wrapperRef = ref<HTMLElement | null>(null)
const visible = ref(false)
const styleState = ref<Record<string, string>>({})

const updatePosition = () => {
  if (!import.meta.client || !wrapperRef.value) {
    return
  }
  const rect = wrapperRef.value.getBoundingClientRect()
  styleState.value = {
    left: `${rect.left + rect.width / 2}px`,
    top: `${Math.max(8, rect.top - 8)}px`
  }
}

const show = () => {
  if (props.disabled || !props.content) {
    return
  }
  updatePosition()
  visible.value = true
}

const hide = () => {
  visible.value = false
}

if (import.meta.client) {
  onMounted(() => {
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
  })
}
</script>

<template>
  <span ref="wrapperRef" class="ui-tooltip-wrap" @mouseenter="show" @mouseleave="hide" @focusin="show" @focusout="hide">
    <slot />
  </span>
  <Teleport to="body">
    <Transition name="tip-fade">
      <div v-if="visible && content" class="ui-tooltip" :style="styleState" role="tooltip">
        {{ content }}
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.ui-tooltip-wrap {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ui-tooltip {
  position: fixed;
  z-index: 2000;
  transform: translate(-50%, -100%);
  max-width: min(440px, calc(100vw - 24px));
  padding: 6px 9px;
  border-radius: 8px;
  border: 0;
  color: var(--text);
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--floating-bg);
  box-shadow: var(--floating-shadow);
  pointer-events: none;
}

.tip-fade-enter-active,
.tip-fade-leave-active {
  transition: opacity 0.14s ease;
}

.tip-fade-enter-from,
.tip-fade-leave-to {
  opacity: 0;
}
</style>
